/**
 * Home › AI cost: what Claude cost in the page's range, the costliest tasks and this month's AI
 * budget. Managers and up (like Admin › AI Usage), from the same /ai/usage read model.
 */
import { type AiUsageResponse, formatMoney } from "@brecx/shared";
import { Link } from "react-router";
import { Icon } from "../components/Icon";
import { ErrorBox, Loading, Pace } from "../components/ui";
import { useApi } from "../lib/api";
import { num } from "../lib/format";

const usd = (m: string, digits = 2) => formatMoney(m, "USD", digits);

export function HomeAiCard({ days, monthFraction }: { days: number; monthFraction: number }) {
  const r = useApi<AiUsageResponse>(`/ai/usage?days=${days}`);
  const d = r.data;
  const cost = d ? Number(d.totals.costMicros) : 0;
  const prev = d ? Number(d.prev.costMicros) : 0;
  const change = prev > 0 ? Math.round(((cost - prev) / prev) * 100) : null;
  return (
    <div className="card">
      <div className="card-h">
        <h2 className="row" style={{ gap: 6 }}>
          <Icon name="spark" size={14} />
          AI cost
        </h2>
        <Link className="hint linkish" to={`/ai-usage?days=${days}`}>
          AI Usage
        </Link>
      </div>
      <div className="card-b stack">
        {r.error ? (
          <ErrorBox error={r.error} retry={r.reload} />
        ) : !d ? (
          <Loading rows={2} />
        ) : (
          <>
            <div>
              <b className="num" style={{ fontSize: 20 }}>
                {usd(d.totals.costMicros)}
              </b>
              <span className="muted" style={{ fontSize: 12 }}>
                {" "}
                last {days} days · {num(d.totals.calls)} call{d.totals.calls === 1 ? "" : "s"}
                {change === null ? "" : ` · ${change > 0 ? "+" : ""}${change}% vs previous ${days} days`}
              </span>
            </div>
            {d.byTask.length ? (
              <table className="tbl compact" aria-label="AI cost by task">
                <tbody>
                  {d.byTask.slice(0, 3).map((g) => (
                    <tr key={g.key}>
                      <td>{g.label}</td>
                      <td className="n">{num(g.calls)}×</td>
                      <td className="n">
                        <b>{usd(g.costMicros)}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                No AI calls in this range.
              </p>
            )}
            <Pace
              label="AI budget this month"
              value={`$${d.budget.spentUsd} of $${d.budget.usd}`}
              frac={d.budget.usedPct / 100}
              expected={monthFraction}
            />
          </>
        )}
      </div>
    </div>
  );
}
